import { apiClient, ApiError } from './client';
import { useAuthStore } from '@/stores/auth-store';
import { getCookie } from '@/lib/cookies';
import type {
  SystemConfig,
  RegistrationConfig,
  ProfileConfig,
  I18nConfig,
  ExperimentalConfig,
  PublicFeatures,
  SystemStatusResponse,
  AuditLog,
  SiteConfig,
  HomepageConfig,
  HomepageStats,
  FooterConfig,
  UiThemeConfig,
} from '@comp-sys/shared';

// System API types
export interface CheckInstalledResponse {
  installed: boolean;
}

export interface InstallRequest {
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  systemName: string;
  locale?: string;
  timezone?: string;
}

export interface InstallResponse {
  success: boolean;
  adminId: string;
}

export interface UpdateConfigRequest {
  key: string;
  value?: Record<string, unknown>;
  description?: string;
}

export interface AuditLogsResponse {
  logs: (AuditLog & {
    user?: { id: string; email: string | null; role: string; displayName: string | null };
    targetUser?: { id: string; email: string | null; role: string; displayName: string | null };
  })[];
  total: number;
  page: number;
  limit: number;
}

export interface QueryAuditLogsParams {
  page?: number;
  limit?: number;
  userId?: string;
  targetUserId?: string;
  action?: string;
  resource?: string;
  startDate?: string;
  endDate?: string;
  /** 关键字搜索(后端 multi-field OR) */
  search?: string;
  /** 维度过滤 */
  competitionId?: string;
  eventId?: string;
  teamId?: string;
  /** AuditResult 枚举值: SUCCESS | FAILURE | DENIED */
  result?: string;
  /** 严重级别: INFO | LOW | MEDIUM | HIGH | CRITICAL */
  severity?: string;
  /** 请求追踪 ID */
  requestId?: string;
}

/** 审计日志聚合参数 */
export interface AggregateAuditLogsParams {
  groupBy?: string;
  bucket?: string;
  startDate?: string;
  endDate?: string;
  competitionId?: string;
}

/** 审计日志聚合响应 */
export interface AggregateAuditLogsResponse {
  groups: { key: string; count: number }[];
  timeseries: { bucket: string; count: number }[];
}

// System API functions
export const systemApi = {
  checkInstalled: () =>
    apiClient.get<CheckInstalledResponse>('system/installed'),

  install: (data: InstallRequest) =>
    apiClient.post<InstallResponse>('system/install', data),

  getAllConfigs: () =>
    apiClient.get<SystemConfig[]>('system/config'),

  getConfig: (key: string) =>
    apiClient.get<SystemConfig>(`system/config/${key}`),

  updateConfig: (data: UpdateConfigRequest) =>
    apiClient.put<SystemConfig>('system/config', data),

  getRegistrationConfig: () =>
    apiClient.get<RegistrationConfig>('system/config/registration'),

  updateRegistrationConfig: (data: RegistrationConfig) =>
    apiClient.put<{ success: boolean }>('system/config/registration', data),

  getProfileConfig: () =>
    apiClient.get<ProfileConfig>('system/config/profile'),

  updateProfileConfig: (data: ProfileConfig) =>
    apiClient.put<{ success: boolean }>('system/config/profile', data),

  getI18nConfig: () =>
    apiClient.get<I18nConfig>('system/config/i18n'),

  updateI18nConfig: (data: I18nConfig) =>
    apiClient.put<{ success: boolean }>('system/config/i18n', data),

  getExperimentalConfig: () =>
    apiClient.get<ExperimentalConfig>('system/config/experimental'),

  /** 已开启的实验性特性(任何登录用户可读;未启用的特性不返回)。非 admin 用这个,不是 getExperimentalConfig。 */
  getPublicFeatures: () =>
    apiClient.get<PublicFeatures>('system/features'),

  updateExperimentalConfig: (data: ExperimentalConfig) =>
    apiClient.put<{ success: boolean }>('system/config/experimental', data),

  getSiteConfig: () =>
    apiClient.get<SiteConfig>('system/config/site'),

  getSystemStatus: () =>
    apiClient.get<SystemStatusResponse>('system/status'),

  getAuditLogs: (params: QueryAuditLogsParams) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.userId) searchParams.set('userId', params.userId);
    if (params.targetUserId) searchParams.set('targetUserId', params.targetUserId);
    if (params.action) searchParams.set('action', params.action);
    if (params.resource) searchParams.set('resource', params.resource);
    if (params.startDate) searchParams.set('startDate', params.startDate);
    if (params.endDate) searchParams.set('endDate', params.endDate);
    if (params.search) searchParams.set('search', params.search);
    if (params.competitionId) searchParams.set('competitionId', params.competitionId);
    if (params.eventId) searchParams.set('eventId', params.eventId);
    if (params.teamId) searchParams.set('teamId', params.teamId);
    if (params.result) searchParams.set('result', params.result);
    if (params.severity) searchParams.set('severity', params.severity);
    if (params.requestId) searchParams.set('requestId', params.requestId);
    const query = searchParams.toString();
    return apiClient.get<AuditLogsResponse>(query ? `system/audit-logs?${query}` : 'system/audit-logs');
  },

  getAuditLog: (id: string) =>
    apiClient.get<AuditLog>(`system/audit-logs/${id}`),

  /**
   * 审计日志聚合(分组 + 时间序列桶)。
   * 由后端 `/system/audit-logs/aggregate` 提供 (P2a)。
   */
  aggregateAuditLogs: (params: AggregateAuditLogsParams) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') searchParams.set(k, String(v));
    });
    const query = searchParams.toString();
    return apiClient.get<AggregateAuditLogsResponse>(
      query ? `system/audit-logs/aggregate?${query}` : 'system/audit-logs/aggregate',
    );
  },

  /**
   * 审计日志导出 (二进制流)。不走 ApiClient(它解 JSON envelope);直接 fetch 取 Blob。
   */
  exportAuditLogs: async (params: QueryAuditLogsParams): Promise<Blob> => {
    const token = useAuthStore.getState().accessToken;
    const baseUrl = import.meta.env.VITE_API_URL || '';
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') searchParams.set(k, String(v));
    });
    const query = searchParams.toString();
    const res = await fetch(`${baseUrl}/api/system/audit-logs/export${query ? `?${query}` : ''}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) throw new Error(`export failed: ${res.status}`);
    return res.blob();
  },

  getHomepageConfig: () =>
    apiClient.get<HomepageConfig>('system/homepage-config'),

  getHomepageStats: () =>
    apiClient.get<HomepageStats>('system/stats'),

  getFooterConfig: () =>
    apiClient.get<FooterConfig>('system/config/footer'),

  updateFooterConfig: (data: Partial<FooterConfig>) =>
    apiClient.put<{ success: boolean }>('system/config/footer', data),

  // UI Theme (system-level appearance / design language)
  getUiThemeConfig: () =>
    apiClient.get<UiThemeConfig>('system/config/ui-theme'),

  updateUiThemeConfig: (theme: string) =>
    apiClient.put<{ success: boolean }>('system/config/ui-theme', { theme }),

  // Import Template
  getImportTemplateInfo: (): Promise<{ storageKey: string; originalName: string; uploadedAt: string } | null> =>
    apiClient.get<{ storageKey: string; originalName: string; uploadedAt: string } | null>('system/import-template'),

  uploadImportTemplate: async (file: File): Promise<{ storageKey: string; originalName: string; uploadedAt: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const storeToken = useAuthStore.getState().accessToken;
    const cookieToken = getCookie('accessToken');
    const accessToken = storeToken ?? cookieToken;
    const baseUrl = import.meta.env.VITE_API_URL || '';
    const response = await fetch(`${baseUrl}/api/system/import-template`, {
      method: 'POST',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiError(response.status, error);
    }
    const result = await response.json();
    if (result && typeof result === 'object' && 'data' in result) return result.data;
    return result;
  },

  deleteImportTemplate: (): Promise<void> =>
    apiClient.delete('system/import-template'),
};
